import { useTranslation } from 'react-i18next'
import React, { useEffect, useRef, useState } from 'react'
import { env } from '../../api/api'
import { navigate } from 'wouter/use-browser-location'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../contexts/store'
import { Op_deletePod, Op_removeUserFromPod } from "../../shared/src/types/Ops"
import { Pod } from '../../shared/src/types/Pod'
import { PodClass } from "../../classes/Pod"

import ConfirmDialog from "../Elements/ConfirmDialog"
import Link from '../Elements/Link'
import NewPodButton from '../Pod/NewPodButton'
import PageWrapper from './PageWrapper'
import UserInfoAvatar from '../Elements/UserInfoAvatar'

import { Alert, Box, Card, CardContent, CardMedia, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import ReplayIcon from '@mui/icons-material/Replay'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'
import { DeleteForever, MoreVert } from '@mui/icons-material'

const Home: React.FC = () => {
  const { t } = useTranslation()
  const { sessionStore, opStore, podStore, uiStore } = useStore()
  const [ anchorEl, setAnchorEl ] = useState<null|Element>(null)
  const [ confirmPodReset, setConfirmPodReset ] = useState<null|string>(null)
  const [ activePod, setActivePod ] = useState<null|Pod>(null)
  const [ showDeleteDialog, setShowDeleteDialog ] = useState<Pod|null>(null)
  const ref = useRef(null)
  const pods = sessionStore.getSortedPods()
  const deviceWidth = uiStore.deviceWidth
  const paddingContent = uiStore.paddingContent
  const nofOwnPods = sessionStore.getOwnPods().length
  const session = sessionStore.session


  useEffect(() => {
    podStore.unsetPod()
  })

  if (!session) return null

  const clearSearch = () => {
    // clear search from last pod
    podStore.clearSearch()
  }

  const handleClick = (event: React.MouseEvent<Element>, pod: Pod) => {
    setAnchorEl(event.currentTarget)
    setActivePod(pod)
    event.stopPropagation()
    event.preventDefault()
  }

  const handleClose = () => {
    setAnchorEl(null)
    setActivePod(null)
  }

  const deletePod = (podId:string) => {
    if (!podId) return
    const pod = session.pods.find(p => p.podId === podId)
    if (!pod) return
    const podUsergroupId = Object.keys(pod.usergroups).find((id) => pod.usergroups[id].role === 'Pod')
    if (!podUsergroupId) return

    const op:Op_deletePod = {
      op: 'deletePod',
      podId: podId,
      data: {
        userId: session.user.userId,
        userName: pod.userInfos[session.user.userId].userName || '',
        usergroupId: podUsergroupId,
      }
    }
    opStore.doOp(op)
  }

  const leavePod = (userId:number, podId:string) => {
    const p = session.pods.find(p => p.podId === podId)
    if (p) {
      const pod = new PodClass(p)
      const op:Op_removeUserFromPod = {
        op: 'removeUserFromPod',
        podId: podId,
        data: {
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          userId,
          userName: pod.userInfos[userId].userName || '',
        }
      }
      opStore.doOp(op)
    }
  }

  const suggestReset = (e:React.MouseEvent, podId:string) => {
    const pod = session.pods.find((pod:Pod) => pod.podId === podId)
    if (pod && pod.outOfSync) {
      setConfirmPodReset(podId)
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const EditMenu = () => (
    <>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={(e) => { if (activePod?.podId) podStore.resetPod(activePod?.podId); handleClose(); e.stopPropagation() }}>
          <ListItemIcon>
            <ReplayIcon />
          </ListItemIcon>
          <ListItemText>{t('Reload this pod')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={(e) => { if (activePod?.podId) leavePod(session.user.userId, activePod?.podId); handleClose(); e.stopPropagation() }}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText>{t('Leave this pod')}</ListItemText>
        </MenuItem>

        { (activePod?.creator?.userId === session.user.userId) ? <nav key="deldiv" aria-label="dangerous delete option">
          <Divider style={{margin:'8px 0'}} />
          <MenuItem key='delete' onClick={(e:React.MouseEvent) => { setShowDeleteDialog(activePod); handleClose(); e.stopPropagation() }}>
            <ListItemIcon>
              <DeleteForever sx={{color:"darkred"}} />
            </ListItemIcon>
            <ListItemText sx={{color:"darkred"}}>{t('Delete this pod')}</ListItemText>
          </MenuItem>
        </nav> : null }
      </Menu>
      <ConfirmDialog
        title={t('Pod Out of Sync')}
        info={t('The Pod seems to be out of sync. To make sure') as string}
        open={Boolean(confirmPodReset)}
        handleClose={() => { setConfirmPodReset(null); navigate(`pod/${confirmPodReset}`, {state: {prevUrl: window.location.pathname}}) }}
        onOk={() => { if (confirmPodReset) podStore.resetPod(confirmPodReset) }}
      />
      <ConfirmDialog title={t('Permanently Delete this Pod?')}
        info={t("Deleting this pod \"{{podname}}\" will also delete all files, interactions, and conversations it contains. All contents of the pod will be lost. This cannot be undone. Are you sure?", {podname: showDeleteDialog?.name}) ||''}
        open={Boolean(showDeleteDialog)}
        handleClose={() => { setShowDeleteDialog(null)} }
        onOk={() => deletePod(showDeleteDialog?.podId || '')}
      />
    </>
  )

  const PodCardHeader = ({pod}: {pod: Pod}) => {
    const headerHeight = 70
    if(pod.podImageFileId && env.backendBaseUrl) return (
      <CardMedia
        component="img"
        height={headerHeight}
        image={`${env.backendBaseUrl}/file/${pod.podImageFileId}`}
        alt="Pod background image"
      />
    )
    if(pod.podColor) return (
      <Box sx={{
        backgroundColor: pod.podColor,
        height: `${headerHeight}px`
      }} />
    )
    else return (
      <Box sx={{
        backgroundColor: "primary.main",
        height: `${headerHeight}px`
      }} />
    )
  }

  const title = t('My Pods')
  // console.log(`Outer component ref:`, ref)

  return (
    <PageWrapper helptext={t('helptext_pod')} title={title}>
      <Box sx={{
        display: "grid",
        gap: "2rem",
        gridTemplateColumns: `repeat(auto-fit, minmax(${deviceWidth === "tiny" ? 200 : 300}px, 1fr))`,
        justifyItems: "center",
        padding: paddingContent ? `${paddingContent}px` : ""
      }}>
        {(pods === null || pods.length === 0) ?
          <Alert severity='info' sx={{
            fontStyle: "italic"
          }}>
            {t('No Pods available.')}
            {(session.user.podLimit > nofOwnPods) && ' ' + t('Add a new one now.')}
          </Alert>
          :
          pods.map(pod => (
            <Link key={pod.podId} noDecoration={true} to={`pod/${pod.podId}/`} sx={{
              display: "grid",
              minHeight: "200px",
              maxWidth: "450px",
              position:'relative',
              width: "100%"
            }}>
              <Card onClick={(e) => {suggestReset(e, pod.podId); clearSearch()}} sx={{
                ":hover": {
                  backgroundColor: "whitesmoke",
                  cursor: "pointer"
                }
              }}>
                <PodCardHeader pod={pod} />
                <CardContent sx={{padding: "16px 32px 24px 16px", position:'relative'}}>
                  <IconButton sx={{position: "absolute", right:'2px', top:'2px', backgroundColor:'#fff8'}} size='small' onClick={(e:React.MouseEvent) => handleClick(e, pod) }>
                    <MoreVert fontSize='small' />
                  </IconButton>
                  {pod.outOfSync &&
                    <Box sx={{position:'absolute', top:2, right:2}} title={t('This pod seems to be out of sync. It is recommended that you reset it. Click for more.') as string}><SyncProblemIcon sx={{backgroundColor:'white', borderRadius:10}} color='warning' /></Box>
                  }
                  <Box sx={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    lineHeight:'1.4rem',
                    paddingRight: "20px"
                  }}>
                    {pod.name}
                  </Box>
                  <Box sx={{
                    fontSize: "15px",
                    fontWeight: 300,
                    minHeight: "50px",
                    padding: "5px 0",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    paddingRight: "15px",
                    whiteSpace: "nowrap"
                  }}>
                    {pod.description}
                  </Box>
                  {pod.status === 'requested' &&
                    <Alert severity='info'>
                      {t('Access requested')}
                    </Alert>
                  }
                </CardContent>
                <Box sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  padding: "4px 16px 16px 16px",
                  wordBreak: "break-word"
                }}>
                  <UserInfoAvatar userId={pod.creator?.userId || 0} podId={pod.podId} sx={{height:24, width:24, fontSize:'80%'}} />
                  <Box>{pod.creator?.userName}</Box>
                </Box>
              </Card>
            </Link>
          ))}
      </Box>
      <Box sx={{
        padding: paddingContent ? `${paddingContent}px` : "",
        paddingBottom: "10px",
        paddingTop: "25px"
      }}>
        <NewPodButton />
      </Box>
      <EditMenu />
    </PageWrapper>
  )
}

export default observer(Home)
